import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  MapPin,
  IndianRupee,
  Building2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import carLoanBanner from "../../assets/ev-loan-banner.webp";
import evLoanMobileBanner from "../../assets/ev-loan-mobile-banner.webp";
import creditCardBanner from "../../assets/personal-loan-banner.webp";
import personalMobileBanner from "../../assets/personal-loan-mobile-banner.webp"
import promoBanner from "../../assets/property-loan-banner.webp";
import propertyMobileBanner from "../../assets/property-loan-mobile-banner.webp"
import goldBanner from "../../assets/gold-loan-banner.webp";
import goldMobileBanner from "../../assets/gold-loan-mobile-banner.webp"
import bussinessBanner from "../../assets/bussiness-loan-banner.webp";
import bussinessMobileBanner from "../../assets/bussiness-loan-mobile-banner.webp"

const banners = [
  {
    id: 1,
    title: "Drive Your Dream Car - Instant EV Loan",
    image: carLoanBanner,
    mobileImage: evLoanMobileBanner,
    link: "/contact",
  },
  {
    id: 2,
    title: "Elevate Your Spending - Smart Credit Cards",
    image: creditCardBanner,
    mobileImage: personalMobileBanner,
    link: "/for-dsas",
  },
  {
    id: 3,
    title: "Instant Clearances & Fast Disbursals",
    image: promoBanner,
    mobileImage: propertyMobileBanner,
    link: "/loan-products",
  },
  {
    id: 4,
    title: "Instant Business Loan",
    image: bussinessBanner,
    mobileImage: bussinessMobileBanner,
    link: "#",
  },
  {
    id: 5,
    title: "Instant Gold Loan",
    image: goldBanner,
    mobileImage: goldMobileBanner,
    link: "#",
  },
];

const stats = [
  {
    value: "50K+",
    label: "Happy Customers",
    icon: <Users className="h-6 w-6 text-[var(--hero-green)] shrink-0 sm:h-7 sm:w-7" strokeWidth={2.2} />,
  },
  {
    value: "120+",
    label: "Cities Covered",
    icon: <MapPin className="h-6 w-6 text-[var(--hero-green)] shrink-0 sm:h-7 sm:w-7" strokeWidth={2.2} />,
  },
  {
    value: "₹500 Cr+",
    label: "Loans Disbursed",
    icon: <IndianRupee className="h-6 w-6 text-[var(--hero-green)] shrink-0 sm:h-7 sm:w-7" strokeWidth={2.2} />,
  },
  {
    value: "25+",
    label: "Lending Partners",
    icon: <Building2 className="h-6 w-6 text-[var(--hero-green)] shrink-0 sm:h-7 sm:w-7" strokeWidth={2.2} />,
  },
];

function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Banner Carousel Auto-play (2 seconds interval)
  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % banners.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + banners.length) % banners.length);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    const bannerInterval = setInterval(() => {
      nextSlide();
    }, 2000);
    return () => clearInterval(bannerInterval);
  }, [isPaused, nextSlide]);

  return (
    <section className="relative overflow-hidden bg-white py-6 sm:py-16 lg:py-20">
      {/* Background Soft Organic Mint Ambient Aura */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-20 top-0 h-[650px] w-[650px] rounded-full bg-[radial-gradient(circle,rgba(46,229,162,0.14),transparent_65%)] blur-2xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-20 top-20 h-[500px] w-[500px] rounded-full bg-[radial-gradient(circle,rgba(242,211,94,0.08),transparent_65%)] blur-2xl"
      />

      <div className="section-container relative z-10">
        <div className="grid grid-cols-1 items-center gap-8 sm:gap-10 lg:grid-cols-12 lg:gap-10 xl:gap-14">
          
          {/* ================= LEFT COLUMN: TITLE & STATS ================= */}
          <div className="order-2 lg:order-1 lg:col-span-6 xl:col-span-6">
            {/* Main Headline */}
            <h1 className="font-syne text-[28px] sm:text-[38px] md:text-[46px] lg:text-[54px] xl:text-[60px] font-bold leading-[1.15] tracking-[-0.5px] sm:tracking-[-1.5px] text-[var(--hero-text-dark)] text-balance">
              Upgrade the Way You Choose <span className="text-[var(--hero-green)]">Loans</span>
            </h1>

            {/* Subtitle */}
            <p className="mt-5 max-w-[540px] font-jakarta text-[16px] leading-[1.65] text-[#4b5563] sm:text-[17px] md:text-[18px]">
              Compare loan options from top lending partners and get the best
              offer that suits your needs.
            </p>

            {/* 2x2 Stats Grid */}
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8">
              {stats.map((st) => (
                <div key={st.label} className="flex items-center gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[var(--hero-green-light)] p-2 border border-[var(--hero-green-border)]">
                    {st.icon}
                  </div>
                  <div>
                    <h3 className="font-bricolage text-[24px] font-bold leading-none text-[var(--hero-text-dark)] sm:text-[28px]">
                      {st.value}
                    </h3>
                    <p className="mt-1 font-jakarta text-[13px] font-medium text-[#6b7280] sm:text-[14px]">
                      {st.label}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ================= RIGHT COLUMN: FULL BANNER IMAGE CAROUSEL ================= */}
          <div
            className="order-1 lg:order-2 lg:col-span-6 xl:col-span-6"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => setIsPaused(false)}
          >
            <div className="relative">
              
              {/* Aspect Ratio Container: 4:3 (1200x900px) on mobile for spacious balanced height, 2:1 on desktop */}
              <div className="relative w-full overflow-hidden rounded-[20px] sm:rounded-[26px] lg:rounded-[28px] border border-[var(--hero-green-border)] shadow-[0_16px_40px_rgba(0,135,81,0.08)] bg-[#f3f8f5] aspect-[4/3] sm:aspect-[2/1]">
                {/* Horizontal Slide Track with Ultra Smooth Easing */}
                <div
                  className="flex h-full w-full transform-gpu transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform"
                  style={{ transform: `translate3d(-${currentSlide * 100}%, 0, 0)` }}
                >
                  {banners.map((banner, idx) => (
                    <div
                      key={banner.id}
                      className="relative h-full w-full shrink-0 basis-full flex-none overflow-hidden"
                    >
                      <Link to={banner.link} className="block h-full w-full">
                        <picture className="block h-full w-full">
                          {banner.mobileImage && (
                            <source
                              media="(max-width: 639px)"
                              srcSet={banner.mobileImage}
                            />
                          )}
                          <img
                            src={banner.image}
                            alt={banner.title}
                            className="h-full w-full object-cover object-center select-none pointer-events-none"
                            loading={idx === 0 ? "eager" : "lazy"}
                            draggable={false}
                          />
                        </picture>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>

              {/* Carousel Pagination Controls */}
              <div className="mt-5 flex items-center justify-between px-1 sm:mt-6">
                {/* Dots */}
                <div className="flex items-center gap-2">
                  {banners.map((_, dotIdx) => (
                    <button
                      key={dotIdx}
                      type="button"
                      onClick={() => setCurrentSlide(dotIdx)}
                      aria-label={`Go to slide ${dotIdx + 1}`}
                      className={`h-2.5 rounded-full transition-all duration-300 cursor-pointer outline-none ${
                        dotIdx === currentSlide
                          ? "w-7 bg-[var(--hero-green)] shadow-xs"
                          : "w-2.5 bg-[#b9d9c8] hover:bg-[var(--hero-green)]/60"
                      }`}
                    />
                  ))}
                </div>

                {/* Arrow Controls with Lucide Icons */}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={prevSlide}
                    aria-label="Previous Banner"
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--hero-green-border)] bg-white text-[var(--hero-green)] shadow-xs transition-all hover:bg-[var(--hero-green)] hover:text-white active:scale-95 cursor-pointer outline-none"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={nextSlide}
                    aria-label="Next Banner"
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--hero-green-border)] bg-white text-[var(--hero-green)] shadow-xs transition-all hover:bg-[var(--hero-green)] hover:text-white active:scale-95 cursor-pointer outline-none"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

export default Hero;